import React, { useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from "classnames";

import styles from "./chat.module.scss";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { useScrollToBottom, useSticky } from 'react-scroll-to-bottom';
import { Podcast, PodcastEditorChatState } from "src/types/coaching/podcast";
import { SimulationEditorChatState } from "src/types/coaching/simulation";
import { createPodcastEditorChat, fetchPodcastTranscriptionForEditChat } from "src/redux/actions/coaching/podcast";


interface ChatContainerProps {
  podcast: Podcast;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ podcast}) => {
  const dispatch = useDispatch();

  const scrollToBottom = useScrollToBottom();
  const [sticky] = useSticky();

  const { session, chats, creating, created, processing } = useSelector((state: any) => {
    const editState = state.coaching.podcasts.edit;
    if (editState === null || editState.item?.id != podcast.id) {
      return {
        session: null,
        chats: []
      }
    }

    const sessionState = editState.editorSession;
    const session = sessionState?.session;
    const chats = sessionState?.chats || [];

    const lastChat = _.last(chats) as PodcastEditorChatState;
    const creating = lastChat?.creating || false;
    const created = lastChat?.created || false;

    const processing = _.reduce(chats, (acc: boolean, chat: PodcastEditorChatState) => {
      return acc || chat.status === 'requested' || chat.status === 'in_progress';
    }, false);

    return {
      session: session,
      chats: chats,
      creating: creating,
      created: created,
      processing: processing
    }
  });

  const handleSubmit = (input: string) => {
    dispatch(createPodcastEditorChat({
      podcast: podcast.id!,
      session: session,
      input: input,
    }));
  }

  useEffect(() => {
    if(created) {
      // handleChange(parameters);
      // TODO -> find the topics and change the topic in the podcast
    }
  }, [created]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.chats}>
          {chats.map((chat: SimulationEditorChatState, index: number) => (
            <ChatView key={index} chat={chat} />
          ))}
        </div>

        <UserInput disabled={creating || processing} onSubmit={handleSubmit} />
      </div>
      {!sticky && (
        <div className={styles.downArrow} onClick={scrollToBottom}>
          <i className={cx(styles.icon, 'paperflite-down-arrow')} />
        </div>
      )}
    </>
  );
}

interface ChatViewProps {
  chat: PodcastEditorChatState;
}

const ChatView: React.FC<ChatViewProps> = ({ chat }) => {
  const dispatch = useDispatch();

  const processing = useMemo(() => {
    return _.includes(['requested', 'in_progress'], chat?.status);
  }, [chat]);

  const hasTranscriptChanges = useMemo(() => {
    return _.get(chat, 'hasTranscriptChanges') || false;
  }, [chat]);

  useEffect(() => {
    if (chat.id && hasTranscriptChanges) {
      dispatch(fetchPodcastTranscriptionForEditChat({
        podcast: chat.podcast!,
        session: chat.session!,
        chat: chat.id
      }));
    }
  }, [chat?.id, hasTranscriptChanges]);

  return (
    <div className={styles.chatView}>
      <div className={styles.userRequest}>
        {chat.input}
      </div>
      <div className={styles.aiResponse}>
        {chat.creating && 'processing your request...'}
        {chat.created && chat.output && processing && (
        <span>Working on it...</span>
        )}
        {chat.created && chat.output && !processing && (
        <span>
          Edits applied successfully
          <i className={cx(styles.icon, "paperflite-check")} />
        </span>
        )}
        {processing && (
        <div className={styles.processing}>
          We're revising your podcast and updating the transcript to reflect your requested changes.
        </div>
        )}
        {chat.created && chat.output && !processing && (
        <span className={styles.processing}>
          Your requested changes have been made. Review the updated transcript before generating the final audio.
        </span>
        )}
      </div>
    </div>
  );
}

interface UserInputProps {
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (input: string) => void;
}

const UserInput: React.FC<UserInputProps> = ({ disabled, onSubmit }) => {
  const [input, setInput] = React.useState("");
  const formRef = React.useRef<HTMLFormElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!disabled && input.trim() !== "") {
      onSubmit(input);
      setInput("");
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  }

  return (
    <div className={styles.userInput}>
      <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
        <i className={cx(styles.icon, 'paperflite-seek')} />
        <input className={styles.input}
          placeholder="Try asking seek..."
          disabled={disabled}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
      </form>
    </div>
  );
}